import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { User, MessageSquare, LogOut, ShieldCheck, ScrollText, Sparkles, Bug, Mail } from "lucide-react";

/**
 * Right-side slide-over Settings drawer.
 *
 * Opens with a soft 280ms transform from the right. The Home screen remains
 * visible underneath; a subtle scrim dims the background but does NOT fully
 * cover it. Closes on scrim tap, Escape, or a rightward swipe.
 *
 * Intentionally minimal: Account / Feedback / Discord — nothing else. The
 * relationship layer (familiarity, tier names, session counts) stays hidden
 * from this drawer too.
 */
const DISCORD_INVITE_URL = "https://discord.gg/44FR6q36W";
export { DISCORD_INVITE_URL };

// Official Discord logo mark (brand SVG).
export const DiscordIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 127.14 96.36" fill="currentColor" aria-hidden="true">
    <path d="M107.7,8.07A105.15,105.15,0,0,0,81.47,0a72.06,72.06,0,0,0-3.36,6.83A97.68,97.68,0,0,0,49,6.83,72.37,72.37,0,0,0,45.64,0,105.89,105.89,0,0,0,19.39,8.09C2.79,32.65-1.71,56.6.54,80.21h0A105.73,105.73,0,0,0,32.71,96.36,77.7,77.7,0,0,0,39.6,85.25a68.42,68.42,0,0,1-10.85-5.18c.91-.66,1.8-1.34,2.66-2a75.57,75.57,0,0,0,64.32,0c.87.71,1.76,1.39,2.66,2a68.68,68.68,0,0,1-10.87,5.19,77,77,0,0,0,6.89,11.1A105.25,105.25,0,0,0,126.6,80.22h0C129.24,52.84,122.09,29.11,107.7,8.07ZM42.45,65.69C36.18,65.69,31,60,31,53s5-12.74,11.43-12.74S54,46,53.89,53,48.84,65.69,42.45,65.69Zm42.24,0C78.41,65.69,73.25,60,73.25,53s5-12.74,11.44-12.74S96.23,46,96.12,53,91.08,65.69,84.69,65.69Z" />
  </svg>
);

export default function SettingsDrawer({ open, onClose }) {
  const { user, logout } = useAuth();
  const dragRef = useRef({ active: false, startX: 0, currentX: 0 });
  const [dragX, setDragX] = useState(0);

  // Esc to close
  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [open, onClose]);

  // Lock body scroll while open
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  useEffect(() => {
    if (open) setDragX(0);
  }, [open]);

  const openDiscord = (e) => {
    e.preventDefault();
    // Iter 89 — live invite. New tab / OS hand-off to the Discord app.
    window.open(DISCORD_INVITE_URL, "_blank", "noopener,noreferrer");
  };

  const moveDragByClientX = (clientX) => {
    if (!dragRef.current.active) return;
    const next = Math.max(0, clientX - dragRef.current.startX);
    dragRef.current.currentX = next;
    setDragX(next);
  };

  const finishDrag = () => {
    if (!dragRef.current.active) return;
    const shouldClose = dragRef.current.currentX > Math.min(window.innerWidth * 0.16, 180);
    dragRef.current = { active: false, startX: 0, currentX: 0 };
    window.removeEventListener("pointermove", windowPointerMove);
    window.removeEventListener("pointerup", windowPointerUp);
    window.removeEventListener("pointercancel", windowPointerUp);
    if (shouldClose) onClose();
    setDragX(0);
  };

  const windowPointerMove = (event) => moveDragByClientX(event.clientX);
  const windowPointerUp = () => finishDrag();

  const startDrag = (e) => {
    const interactive = e.target.closest("a, button, input, textarea, select, [role='button']");
    if (interactive) {
      dragRef.current = { active: false, startX: 0, currentX: 0 };
      return;
    }
    dragRef.current = { active: true, startX: e.clientX, currentX: 0 };
    setDragX(0);
    window.addEventListener("pointermove", windowPointerMove);
    window.addEventListener("pointerup", windowPointerUp, { once: true });
    window.addEventListener("pointercancel", windowPointerUp, { once: true });
  };

  const moveDrag = (e) => {
    moveDragByClientX(e.clientX);
  };

  const endDrag = () => {
    finishDrag();
  };

  return (
    <div
      aria-hidden={!open}
      data-testid="settings-drawer-root"
      className={`fixed inset-0 z-40 ${open ? "pointer-events-auto" : "pointer-events-none"}`}
    >
      {/* Dim scrim (subtle — Home stays visible underneath) */}
      <div
        onClick={onClose}
        data-testid="settings-drawer-scrim"
        className={`absolute inset-0 bg-[#080716]/55 transition-opacity duration-300 ${
          open ? "opacity-100" : "opacity-0"
        }`}
      />

      {/* Drawer panel — 40-50% width on desktop, 86vw on mobile */}
      <aside
        role="dialog"
        aria-label="Settings"
        data-testid="settings-drawer-panel"
        onPointerDown={startDrag}
        onPointerMove={moveDrag}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        className={`absolute top-0 right-0 h-full w-[86vw] sm:w-[46vw] md:w-[44vw] lg:w-[42vw] xl:w-[40vw] max-w-[860px]
                    border-l border-violet-200/12 bg-[#0e0c20]/92 backdrop-blur-xl
                    touch-pan-y select-none transition-transform duration-300 ease-[cubic-bezier(0.22,0.61,0.36,1)] ${
                      open ? "translate-x-0" : "translate-x-full"
                    }`}
        style={open && dragX > 0 ? { transform: `translateX(${dragX}px)`, transitionDuration: "0ms" } : undefined}
      >
        {/* Header — safe-area top padding so the Dynamic Island / status bar
            never overlaps the drawer swipe handle on iOS 14+ devices. */}
        <header
          className="flex items-center justify-between px-6 py-5"
          style={{ paddingTop: "calc(env(safe-area-inset-top, 0px) + 1.25rem)" }}
        >
          <span className="text-xs uppercase tracking-[0.32em] text-violet-300/65">
            Settings
          </span>
          <span
            className="h-1 w-10 rounded-full bg-violet-200/18"
            aria-hidden="true"
            data-testid="settings-drawer-swipe-handle"
          />
        </header>

        {/* Sections — scrollable so the footer stays reachable */}
        <nav
          className="px-3 py-2 space-y-1 overflow-y-auto"
          style={{ maxHeight: "calc(100% - 10rem - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px))" }}
        >
          <DrawerLink
            to="/settings"
            icon={User}
            label="Account"
            sublabel={user?.email || "Manage your account"}
            testid="settings-drawer-account"
            onNavigate={onClose}
          />
          <DrawerButton
            onClick={openDiscord}
            icon={DiscordIcon}
            label="Discord"
            sublabel="Join our community"
            testid="settings-drawer-discord"
          />

          <SectionLabel>Privacy</SectionLabel>
          <DrawerLink
            to="/legal/privacy"
            icon={ShieldCheck}
            label="Privacy Policy"
            sublabel="What we collect and why"
            testid="settings-drawer-privacy-policy"
            onNavigate={onClose}
          />
          <DrawerLink
            to="/legal/terms"
            icon={ScrollText}
            label="Terms of Service"
            sublabel="The agreement between us"
            testid="settings-drawer-terms"
            onNavigate={onClose}
          />
          <DrawerLink
            to="/legal/ai-disclaimer"
            icon={Sparkles}
            label="AI Disclaimer"
            sublabel="What your companion is — and isn't"
            testid="settings-drawer-ai-disclaimer"
            onNavigate={onClose}
          />

          <SectionLabel>Support</SectionLabel>
          <DrawerLink
            to="/legal/support"
            icon={Mail}
            label="Contact"
            sublabel="Coming Soon"
            testid="settings-drawer-contact"
            onNavigate={onClose}
          />
          <DrawerLink
            to="/legal/support"
            icon={Bug}
            label="Report a Bug"
            sublabel="Coming Soon"
            testid="settings-drawer-report-bug"
            onNavigate={onClose}
          />
          <DrawerLink
            to="/legal/support"
            icon={MessageSquare}
            label="Feedback"
            sublabel="Coming Soon"
            testid="settings-drawer-feedback"
            onNavigate={onClose}
          />
        </nav>

        {/* Footer — kept above the iOS Home Indicator */}
        <div className="absolute inset-x-3" style={{ bottom: "calc(1.5rem + env(safe-area-inset-bottom, 0px))" }}>
          <button
            type="button"
            onClick={async () => { await logout(); onClose(); }}
            data-testid="settings-drawer-logout"
            className="w-full flex items-center gap-3 px-4 py-3 rounded-2xl text-violet-300/55 hover:text-violet-100 hover:bg-violet-200/4 transition-colors"
          >
            <LogOut className="w-4 h-4" strokeWidth={1.5} />
            <span className="text-sm">Sign out</span>
          </button>
        </div>
      </aside>
    </div>
  );
}

const rowBase =
  "w-full flex items-start gap-3 px-4 py-4 rounded-2xl text-left transition-colors hover:bg-violet-200/4";

const SectionLabel = ({ children }) => (
  <p className="pt-5 pb-1 px-4 text-[10px] uppercase tracking-[0.32em] text-violet-300/45 border-t border-violet-200/10 mt-3">
    {children}
  </p>
);

const DrawerLink = ({ to, icon: Icon, label, sublabel, testid, onNavigate }) => (
  <Link to={to} onClick={onNavigate} data-testid={testid} className={rowBase}>
    <Icon className="w-4 h-4 mt-1 text-violet-200/80 flex-shrink-0" strokeWidth={1.5} />
    <span className="flex-1 min-w-0">
      <span className="block text-violet-50 text-base">{label}</span>
      <span className="block text-xs text-violet-300/55 truncate mt-0.5">{sublabel}</span>
    </span>
  </Link>
);

const DrawerButton = ({ onClick, icon: Icon, label, sublabel, testid }) => (
  <button type="button" onClick={onClick} data-testid={testid} className={rowBase}>
    <Icon className="w-4 h-4 mt-1 text-violet-200/80 flex-shrink-0" strokeWidth={1.5} />
    <span className="flex-1 min-w-0">
      <span className="block text-violet-50 text-base">{label}</span>
      <span className="block text-xs text-violet-300/55 truncate mt-0.5">{sublabel}</span>
    </span>
  </button>
);

